import {PrimerComponentTestimonialSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentTestimonial'

import {defineBrandPage, optionalSection} from '../../brand/components/BrandPage'
import {
  findByIdWithSchema,
  findByContentType,
  GenericContentSchema,
  GenericGroupSchema,
  GenericSectionSchema,
  parseEntries,
  requireById,
  type GenericSection,
} from '../../brand/lib/types/contentful'

export const homePage = defineBrandPage({
  automation: optionalSection('automationSection', GenericSectionSchema, toHomeAutomationContent),
  collaboration: optionalSection('collaborationSection', GenericSectionSchema, toHomeCollaborationContent),
  cta: optionalSection('ctaSection', GenericSectionSchema, toHomeCtaContent),
  customerStories: optionalSection('customerStoriesSection', GenericSectionSchema, toHomeCustomerStoriesContent),
  hero: optionalSection('heroSection', GenericSectionSchema, toHomeHeroContent),
  security: optionalSection('securitySection', GenericSectionSchema, toHomeSecurityContent),
})

export function toHomeHeroContent(heroSection: GenericSection) {
  const content = heroSection.fields.content
  const carousel = findByIdWithSchema(content, 'heroCarousel', GenericGroupSchema)

  return {
    carouselItems: carousel ? parseEntries(carousel.fields.content, GenericContentSchema) : undefined,
    content: findByIdWithSchema(content, 'heroContent', GenericContentSchema),
    logos: findByContentType(content, 'primerComponentLogoSuite'),
  }
}

export function toHomeAutomationContent(automationSection: GenericSection) {
  const content = automationSection.fields.content
  const breakout = requireById(content, 'automationSectionBreakout', GenericContentSchema)
  const accordion = requireById(content, 'automationSectionRiverAccordion', GenericGroupSchema)

  return {
    accordionItems: parseEntries(accordion.fields.content, GenericContentSchema),
    breakout,
    breakoutContent: parseEntries(breakout.fields.content, GenericContentSchema),
    sectionIntro: automationSection.fields.sectionIntro,
  }
}

export function toHomeCollaborationContent(collaborationSection: GenericSection) {
  const content = collaborationSection.fields.content
  const breakout = requireById(content, 'collaborationSectionBreakout', GenericContentSchema)
  const accordion = requireById(content, 'collaborationSectionRiverAccordion', GenericGroupSchema)
  const testimonial = findByContentType(breakout.fields.content, 'primerComponentTestimonial')

  return {
    accordionItems: parseEntries(accordion.fields.content, GenericContentSchema),
    breakout,
    sectionIntro: collaborationSection.fields.sectionIntro,
    testimonial: testimonial ? PrimerComponentTestimonialSchema.parse(testimonial) : undefined,
  }
}

export function toHomeCustomerStoriesContent(customerStoriesSection: GenericSection) {
  const content = customerStoriesSection.fields.content
  const stories = findByIdWithSchema(content, 'customerStoriesSectionStories', GenericGroupSchema)

  return {
    content: findByIdWithSchema(content, 'customerStoriesSectionContent', GenericContentSchema),
    storyCategories: stories
      ? parseEntries(stories.fields.content, GenericContentSchema).map(category => ({
          category,
          stories: parseEntries(category.fields.content, GenericContentSchema),
        }))
      : undefined,
  }
}

export function toHomeSecurityContent(securitySection: GenericSection) {
  const content = securitySection.fields.content
  const features = findByIdWithSchema(content, 'securitySectionFeatures', GenericGroupSchema)
  const stats = findByIdWithSchema(content, 'securitySectionStats', GenericGroupSchema)

  return {
    breakout: findByIdWithSchema(content, 'securitySectionBreakout', GenericContentSchema),
    features: features ? parseEntries(features.fields.content, GenericContentSchema) : undefined,
    sectionIntro: securitySection.fields.sectionIntro,
    stats: stats ? parseEntries(stats.fields.content, GenericContentSchema) : undefined,
  }
}

export function toHomeCtaContent(ctaSection: GenericSection) {
  return findByIdWithSchema(ctaSection.fields.content, 'ctaSectionContent', GenericContentSchema)
}

export type HomeAutomationContent = ReturnType<typeof toHomeAutomationContent>
export type HomeCollaborationContent = ReturnType<typeof toHomeCollaborationContent>
export type HomeCustomerStoriesContent = ReturnType<typeof toHomeCustomerStoriesContent>
export type HomeHeroContent = ReturnType<typeof toHomeHeroContent>
export type HomeSecurityContent = ReturnType<typeof toHomeSecurityContent>
